import React, { useState, useEffect } from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../context/AuthContext';
import { useTeams } from '../context/TeamsContext';
import { ENV } from '../utils/env';
import { getPrivacyConsent } from '../services/api';
import PrivacyPolicyModal from './PrivacyPolicyModal';

interface ProtectedRouteProps extends RouteProps {
  component: React.ComponentType<any>;
  roles?: string[]; // Optional roles for role-based access control
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  roles,
  ...rest
}) => {
  const { isAuthenticated, user, getAccessToken } = useAuth();
  const { accounts, instance } = useMsal();
  const { isInTeams } = useTeams();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyConsentChecked, setPrivacyConsentChecked] = useState(false);
  const [hasPrivacyConsent, setHasPrivacyConsent] = useState(false);

  // Path to redirect unauthenticated users
  const loginPath = ENV.LOGIN_PATH;
  const unauthorizedPath = ENV.UNAUTHORIZED_PATH;

  // Check privacy consent when user is authenticated
  useEffect(() => {
    const checkPrivacyConsent = async () => {
      if (isAuthenticated && user && !privacyConsentChecked) {
        // Skip consent check for Teams users - they've already consented through Teams SSO
        if (isInTeams) {
          setHasPrivacyConsent(true);
          setPrivacyConsentChecked(true);
          return;
        }

        try {
          // Get current generative AI policy version
          const policyResponse = await fetch('/privacy-policy.md');
          const policyContent = await policyResponse.text();

          let currentVersion = '1.0';
          const frontmatterMatch = policyContent.match(/^---\n([\s\S]*?)\n---\n/);
          if (frontmatterMatch) {
            const versionMatch = frontmatterMatch[1].match(/version:\s*["']?([^"'\n]+)["']?/);
            if (versionMatch) {
              currentVersion = versionMatch[1].trim();
            }
          }

          // Check local storage first
          const cachedConsent = localStorage.getItem('privacy_consent_given');
          const cachedVersion = localStorage.getItem('privacy_consent_version') || '1.0';

          if (cachedConsent === 'true' && cachedVersion === currentVersion) {
            setHasPrivacyConsent(true);
            setPrivacyConsentChecked(true);
            return;
          }

          // If not cached or version mismatch, check with backend
          await getAccessToken();
          const consentData = await getPrivacyConsent(user.localAccountId);

          if (consentData.has_consented && consentData.privacy_policy_version === currentVersion) {
            setHasPrivacyConsent(true);
            localStorage.setItem('privacy_consent_given', 'true');
            localStorage.setItem('privacy_consent_version', currentVersion);
          } else {
            // Need new consent for current version
            setShowPrivacyModal(true);
          }
        } catch (error) {
          console.error('Error checking privacy consent:', error);
          setShowPrivacyModal(true);
        } finally {
          setPrivacyConsentChecked(true);
        }
      }
    };

    checkPrivacyConsent();
  }, [isAuthenticated, user?.localAccountId, isInTeams]);

  const handlePrivacyConsent = () => {
    setHasPrivacyConsent(true);
    setShowPrivacyModal(false);
  };

  // Custom authentication check function
  const isUserAuthenticated = () => {
    // Check our auth context first
    if (isAuthenticated && user) {
      return true;
    }

    // Then check MSAL accounts
    if (accounts.length > 0) {
      // If we have accounts but AuthContext doesn't know about them,
      // set the active account to help synchronize state
      instance.setActiveAccount(accounts[0]);
      return true;
    }

    // Finally check for any active account
    const activeAccount = instance.getActiveAccount();
    return !!activeAccount;
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        // Perform comprehensive authentication check
        const authenticated = isUserAuthenticated();

        // First check if the user is authenticated
        if (!authenticated) {
          return <Redirect to={{ pathname: loginPath, state: { from: props.location } }} />;
        }

        // If roles are specified, check if the user has any of the required roles
        // This requires the backend to include roles in the token
        if (roles && roles.length > 0 && roles[0] !== '') {
          // Get user roles from our extended user object
          const userRoles = (user as any)?.roles || [];

          // Use default role as fallback if user object doesn't have roles yet
          // This helps during the initialization phase
          if (userRoles.length === 0 && authenticated) {
            return <Component {...props} />;
          }

          // Allow access if the user has Admin role or any of the specified roles
          const hasRequiredRole = userRoles.includes('Admin') ||
            roles.some(role => userRoles.includes(role));

          if (!hasRequiredRole) {
            return <Redirect to={{ pathname: unauthorizedPath }} />;
          }
        }

        // If authenticated and has required role, check privacy consent
        if (privacyConsentChecked && !hasPrivacyConsent) {
          return (
            <>
              <PrivacyPolicyModal 
                isOpen={showPrivacyModal} 
                onConsent={handlePrivacyConsent}
              />
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <p>Checking generative AI policy consent...</p>
              </div>
            </>
          );
        }

        // If authenticated, has required role, and has privacy consent, render the component
        if (privacyConsentChecked && hasPrivacyConsent) {
          return <Component {...props} />;
        }

        // Show loading while checking consent
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Loading...</p>
          </div>
        );
      }}
    />
  );
};

export default ProtectedRoute;